import { useState } from "react";
import { useTranslation } from "react-i18next";
import beachCoinsArtwork from "../../../../../../../assets/static/images/symbols/v1/symbol-golden-beach-coins.webp";
import footwear001Artwork from "../../../../../../../assets/static/images/commerce/chain-offer/footwear-001-stylised.png";
import energyCanArtwork from "../../../../../../../assets/static/images/commerce/chain-offer/card-step-single-energy-can.png";
import lockArtwork from "../../../../../../../assets/static/images/commerce/chain-offer/card-step-lock.png";
import completedCheckmarkArtwork from "../../../../../../../assets/static/images/commerce/chain-offer/chain-offer__step-completed-checkmark.webp";
import headwear006Artwork from "../../../../../../../assets/static/images/commerce/chain-offer/headwear-006-stylised.png";
import shirt002Artwork from "../../../../../../../assets/static/images/commerce/chain-offer/shirt-002-stylised.png";
import CloudDecoration from "../../../../../../../assets/static/images/commerce/chain-offer/cloud-bg-deco.svg?react";
import StonesDecoration from "../../../../../../../assets/static/images/commerce/chain-offer/stones-bg-deco.svg?react";
import TelegramStar from "../../../../../../../assets/static/images/commerce/chain-offer/telegram-star.svg?react";
import {
    formatCommercePrice,
    formatIntegerAmount,
    telegramStarsPrice,
} from "../../../../../../../logic/helpers/commerce";
import type {
    ChainOfferBoardPhase,
    ChainOfferStep,
} from "../../../../../../../logic/helpers/commerceChainPresentation";
import { useFitContentWidth } from "../../../../../../../logic/hooks/useFitContentWidth";
import {
    ButtonVar1,
    type ButtonVar1Colors,
} from "../../../../../../common/ButtonVar1/ButtonVar1";
import "./ChainOfferStepCard.scss";

interface ChainOfferStepCardProps {
    step: ChainOfferStep;
    slotIndex: number;
    locale: string;
    boardPhase: ChainOfferBoardPhase;
    isBusy: boolean;
    isExpired: boolean;
    isPresentationOnly?: boolean;
    onActivate: (step: ChainOfferStep) => void;
}

type RewardPreview = ChainOfferStep["offer"]["rewards"][number];

const AVAILABLE_PRICE_BUTTON_COLORS: ButtonVar1Colors = {
    containerStroke: "#035318",
    containerFill: `linear-gradient(
        to bottom,
        #2eaf25 0%,
        #0c730a 63.9435%,
        #036501 73.99%,
        #056604 84.8566%,
        #057900 93.5704%,
        #0e7508 96.9534%,
        #1b711a 100%
    )`,
    faceStroke: `linear-gradient(
        to bottom,
        #63d432 0%,
        #4fc91a 11.036%,
        #48c11a 22.191%,
        #53cb26 32.4973%,
        #4bc524 42.3184%,
        #4cbd30 52.8672%,
        #35c016 61.7184%,
        #40c21a 69.4783%,
        #34bd0f 78.3296%,
        #31be0a 88.272%,
        #34c510 93.8495%,
        #2cb90c 100%
    )`,
    faceFill: `linear-gradient(
        to bottom,
        #34b704 0%,
        #34b704 4.5124%,
        #2ab706 9.8261%,
        #24b404 17.5793%,
        #22b705 28.6554%,
        #18b502 39.2568%,
        #0ab505 50.9335%,
        #09b406 62.0798%,
        #0ab203 73.9427%,
        #0bb305 85.778%,
        #13b101 95.3827%,
        #13b101 100%
    )`,
};

function compactIntegerAmount(amount: string, locale: string): string {
    return new Intl.NumberFormat(locale, {
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(BigInt(amount));
}

function rewardArtworkForItem(itemCode: string | null): string | null {
    if (itemCode === "headwear-006") return headwear006Artwork;
    if (itemCode === "shirt-002") return shirt002Artwork;
    if (itemCode === "footwear-001") return footwear001Artwork;
    return null;
}

function rewardLabel(
    reward: RewardPreview,
    locale: string,
    t: ReturnType<typeof useTranslation>["t"],
): string {
    const amount = formatIntegerAmount(reward.amount, locale);
    if (reward.kind === "SM_ENERGY") {
        return t("commerce.rewards.energy", { amount });
    }
    if (reward.kind === "CURRENCY") {
        return t("commerce.rewards.currency", {
            amount,
            currency: reward.currencyCode ?? "",
        });
    }
    return t("commerce.rewards.item", { amount });
}

function cardStateLabel(
    step: ChainOfferStep,
    locale: string,
    t: ReturnType<typeof useTranslation>["t"],
): string {
    if (step.state === "CLAIMED") return t("commerce.chain.claimed");
    if (step.state === "RESTRICTED") return t("commerce.chain.locked");
    if (step.action === "FREE_CLAIM") return t("commerce.chain.free");

    const price = telegramStarsPrice(step.offer);
    return price
        ? formatCommercePrice(price, locale)
        : t("commerce.chain.unavailable");
}

export function ChainOfferStepCard({
    step,
    slotIndex,
    locale,
    boardPhase,
    isBusy,
    isExpired,
    isPresentationOnly = false,
    onActivate,
}: ChainOfferStepCardProps) {
    const { t } = useTranslation();
    const [lockedFeedbackRevision, setLockedFeedbackRevision] = useState(0);
    const [failedRewardImageUrl, setFailedRewardImageUrl] = useState<
        string | null
    >(null);
    const primaryReward = step.offer.rewards[0] ?? null;
    const isEquipment =
        primaryReward !== null &&
        (primaryReward.kind === "ITEM_ENTITLEMENT" ||
            primaryReward.kind === "ITEM_INSTANCE" ||
            primaryReward.kind === "ITEM_STACK");
    const isCompleting =
        boardPhase === "completing" && step.state === "AVAILABLE";
    const isVisuallyClaimed =
        boardPhase === "advancing" && step.state === "AVAILABLE";
    const isUnlocking =
        boardPhase === "unlocking" && step.state === "AVAILABLE";
    const visualState = isCompleting
        ? "completing"
        : isVisuallyClaimed
          ? "claimed"
          : isUnlocking
            ? "unlocking"
            : step.state.toLowerCase();
    const price = telegramStarsPrice(step.offer);
    const showsCheck =
        step.state === "CLAIMED" || isCompleting || isVisuallyClaimed;
    const showsStars = step.action !== "FREE_CLAIM" && price !== null;
    const priceLabel =
        step.action === "FREE_CLAIM"
            ? t("commerce.chain.free")
            : price
              ? formatIntegerAmount(price.unitAmount, locale)
              : t("commerce.chain.unavailable");
    const { containerRef: priceContainerRef, contentRef: priceContentRef } =
        useFitContentWidth(
            `${locale}:${showsCheck}:${showsStars}:${priceLabel}`,
        );
    const isActionable =
        step.state === "AVAILABLE" &&
        boardPhase === "idle" &&
        !isPresentationOnly &&
        !isBusy &&
        !isExpired &&
        (step.action === "FREE_CLAIM" || price !== null);
    const isRestrictedInteractive =
        step.state === "RESTRICTED" && boardPhase === "idle" && !isBusy;
    const rewardDescription = primaryReward
        ? rewardLabel(primaryReward, locale, t)
        : t("commerce.rewards.unknown");
    const stateDescription = cardStateLabel(step, locale, t);
    const accessibleLabel = t("commerce.chain.stepAriaLabel", {
        step: step.ordinal,
        reward: rewardDescription,
        state: stateDescription,
    });
    const lockedFeedbackClass =
        lockedFeedbackRevision === 0
            ? null
            : lockedFeedbackRevision % 2 === 0
              ? "is-locked-feedback-even"
              : "is-locked-feedback-odd";

    const handleClick = (): void => {
        if (isRestrictedInteractive) {
            setLockedFeedbackRevision((current) => current + 1);
            return;
        }
        if (isActionable) onActivate(step);
    };

    const itemArtwork = primaryReward
        ? rewardArtworkForItem(primaryReward.itemCode)
        : null;
    const showsBeachCoins =
        primaryReward?.kind === "CURRENCY" &&
        primaryReward.currencyCode === "BEACH_COINS";
    const isEnergy = primaryReward?.kind === "SM_ENERGY";
    const energyCanCount = primaryReward?.amount === "100" ? 2 : 1;
    const remoteRewardImage = step.presentation.rewardImage ?? null;
    const showsRemoteRewardImage =
        remoteRewardImage !== null &&
        remoteRewardImage.url !== failedRewardImageUrl;

    return (
        <li
            className="chain-offer-step-card"
            data-slot={slotIndex + 1}
            data-theme={isEquipment ? "equipment" : "consumable"}
            data-step-state={visualState}
        >
            <div
                className={[
                    "chain-offer-step-card__visual",
                    lockedFeedbackClass,
                ]
                    .filter(Boolean)
                    .join(" ")}
            >
                {isEquipment ? (
                    <StonesDecoration
                        className="chain-offer-step-card__decoration chain-offer-step-card__decoration--stones"
                        aria-hidden="true"
                    />
                ) : (
                    <CloudDecoration
                        className="chain-offer-step-card__decoration chain-offer-step-card__decoration--cloud"
                        aria-hidden="true"
                    />
                )}

                <span className="chain-offer-step-card__reward-panel">
                    {showsRemoteRewardImage ? (
                        <img
                            className="chain-offer-step-card__reward-art"
                            src={remoteRewardImage.url}
                            width={remoteRewardImage.width}
                            height={remoteRewardImage.height}
                            alt=""
                            aria-hidden="true"
                            decoding="async"
                            draggable={false}
                            referrerPolicy="no-referrer"
                            onError={() =>
                                setFailedRewardImageUrl(remoteRewardImage.url)
                            }
                        />
                    ) : null}
                    {!showsRemoteRewardImage && isEnergy && primaryReward && (
                        <span
                            className="chain-offer-step-card__energy-art"
                            data-can-count={energyCanCount}
                            aria-hidden="true"
                        >
                            {Array.from({ length: energyCanCount }).map(
                                (_, index) => (
                                    <img
                                        key={index}
                                        src={energyCanArtwork}
                                        alt=""
                                        draggable={false}
                                    />
                                ),
                            )}
                        </span>
                    )}
                    {!showsRemoteRewardImage && itemArtwork && (
                        <img
                            className="chain-offer-step-card__item-art"
                            src={itemArtwork}
                            alt=""
                            aria-hidden="true"
                            draggable={false}
                        />
                    )}
                    {!showsRemoteRewardImage && showsBeachCoins && (
                        <img
                            className="chain-offer-step-card__coin-art"
                            src={beachCoinsArtwork}
                            alt=""
                            aria-hidden="true"
                            draggable={false}
                        />
                    )}
                    {primaryReward && (isEnergy || showsBeachCoins) && (
                        <span className="chain-offer-step-card__reward-amount">
                            {showsBeachCoins
                                ? compactIntegerAmount(
                                      primaryReward.amount,
                                      locale,
                                  )
                                : formatIntegerAmount(
                                      primaryReward.amount,
                                      locale,
                                  )}
                        </span>
                    )}
                    {primaryReward &&
                        isEquipment &&
                        primaryReward.amount !== "1" && (
                            <span className="chain-offer-step-card__item-amount">
                                ×
                                {formatIntegerAmount(
                                    primaryReward.amount,
                                    locale,
                                )}
                            </span>
                        )}
                    {primaryReward === null && (
                        <span className="chain-offer-step-card__fallback-reward">
                            {t("commerce.rewards.unknown")}
                        </span>
                    )}
                </span>

                <ButtonVar1
                    className="chain-offer-step-card__price-bar"
                    colors={AVAILABLE_PRICE_BUTTON_COLORS}
                    content={
                        <span
                            className="chain-offer-step-card__price-content"
                            ref={priceContainerRef}
                        >
                            {showsCheck ? (
                                <img
                                    className="chain-offer-step-card__check"
                                    src={completedCheckmarkArtwork}
                                    alt=""
                                    aria-hidden="true"
                                    draggable={false}
                                />
                            ) : (
                                <span
                                    className="chain-offer-step-card__price-fit"
                                    ref={priceContentRef}
                                >
                                    {showsStars && (
                                        <TelegramStar
                                            className="chain-offer-step-card__price-icon"
                                            aria-hidden="true"
                                        />
                                    )}
                                    <span className="chain-offer-step-card__price-label">
                                        {priceLabel}
                                    </span>
                                </span>
                            )}
                        </span>
                    }
                    aria-label={accessibleLabel}
                    aria-current={
                        step.state === "AVAILABLE" ? "step" : undefined
                    }
                    aria-disabled={!isActionable}
                    tabIndex={isPresentationOnly ? -1 : undefined}
                    disabled={
                        isPresentationOnly ||
                        step.state === "CLAIMED" ||
                        isCompleting ||
                        isUnlocking ||
                        (step.state === "AVAILABLE" && !isActionable)
                    }
                    dir="ltr"
                    onClick={handleClick}
                />

                {(step.state === "RESTRICTED" || isUnlocking) && (
                    <img
                        className="chain-offer-step-card__lock"
                        src={lockArtwork}
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                    />
                )}
            </div>
        </li>
    );
}
